import { memo, useMemo } from 'react'
import type { Divisao } from '../api'
import { blocosDoPodio, marcoPorExtenso, podioDasMarcas, PASSO_DO_MARCO } from '../marcos'
import { valorCurto } from '../formato'
import { Confetes } from './Confetes'

/**
 * A comemoração de cada milhão do total geral.
 *
 * Tela cheia por 15 segundos, decidido em 13/10: o total em letras enormes, o
 * milhão batido por extenso, o primeiro representante de CADA marca com o valor
 * dele, e confete caindo por cima. A rotação fica pausada enquanto ela dura,
 * para ninguém perder a marca que estava na tela.
 *
 * A METADE da meta (R$ 14 mi) e a META (R$ 28 mi) usam a mesma tela, com três
 * diferenças: duram 20 s em vez de 12, jogam o dobro de confete e trocam o
 * `SEIS MILHÕES!` por `ESTAMOS NA METADE!` ou `META BATIDA!`, com o valor dito
 * por escrito. Elas acontecem uma vez cada, e o telão precisa dizer isso sem
 * ninguém explicar.
 *
 * Três decisões que o telão impôs:
 *
 *  1. **O confete é CSS, não canvas.** São 60 divs com `animation` e nenhum
 *     `requestAnimationFrame`: a animação roda na composição do navegador, fora
 *     da thread do JavaScript, e por isso ela não disputa com o SSE nem trava a
 *     chegada do snapshot seguinte. Num telão ligado há dias, laço de animação
 *     em JS é o tipo de coisa que degrada devagar e ninguém percebe.
 *  2. **Nada é consultado aqui.** O pódio sai do `painel` que a tela já tem
 *     (`topRepresentante` por divisão, que vem no `/api/painel`): a comemoração
 *     não pode depender de oito requisições no instante em que dispara.
 *  3. **O número grande é o TOTAL REAL, não o marco redondo.** Mostra-se
 *     `R$ 6.014 mil` — quanto a War Room vendeu neste instante —, e o milhão
 *     cruzado aparece por extenso logo abaixo (`SEIS MILHÕES!`). Foi pedido
 *     assim: o que a sala quer ver grande é o total, e o marco é a razão da
 *     festa. Se o total chegar `null` (ninguém mediu), cai no marco redondo,
 *     que é a única coisa que se sabe de certo naquele momento.
 */

/* 60 peças no milhão e 120 na metade e na meta. A geração saiu para
   `confete.ts` em 14/10 (a festa por marca usa 80 e 160, e a paleta dourada):
   a quantidade é parâmetro, e as peças seguem determinísticas — mesmo índice,
   mesma peça, em todo telão. */
const CONFETES_MILHAO = 60
const CONFETES_DOBRADOS = 120

type Props = {
  /** O marco batido: 6 para o sexto milhão. */
  marco: number
  /** O total geral AGORA, que é o número grande da tela. */
  total: number | null
  /** `true` quando o que caiu foi a META GERAL, e não só mais um milhão. */
  meta: boolean
  /** `true` quando o que caiu foi a METADE da meta. */
  metade: boolean
  /** A meta do cadastro, dita por escrito na comemoração dela. */
  metaGeral: number | null
  divisoes: Divisao[]
}

function ComemoracaoBase({ marco, total, meta, metade, metaGeral, divisoes }: Props) {
  const blocos = useMemo(() => blocosDoPodio(podioDasMarcas(divisoes)), [divisoes])

  return (
    /* `role="status"` e não `alert`: é uma comemoração, não um aviso de erro. */
    <div
      className={
        meta ? 'comemoracao na-meta' : metade ? 'comemoracao na-metade' : 'comemoracao'
      }
      role="status"
    >
      <Confetes quantidade={meta || metade ? CONFETES_DOBRADOS : CONFETES_MILHAO} />
      <div className="comemoracao-miolo">
        <div className="comemoracao-rotulo">META VENDAS</div>
        <div className="comemoracao-valor">
          {typeof total === 'number' && Number.isFinite(total)
            ? valorCurto(total)
            : valorCurto(marco * PASSO_DO_MARCO)}
        </div>
        {meta ? (
          <>
            <div className="comemoracao-extenso">META BATIDA!</div>
            {/* A meta aparece por escrito: é ela que dá sentido ao "batida", e
                de longe a sala lê a frase antes de conferir o número grande. */}
            <div className="comemoracao-meta-dita">
              100% DE {valorCurto(metaGeral ?? marco * PASSO_DO_MARCO)}
            </div>
          </>
        ) : metade ? (
          <>
            <div className="comemoracao-extenso">ESTAMOS NA METADE!</div>
            <div className="comemoracao-meta-dita">
              50% DE {valorCurto(metaGeral ?? marco * PASSO_DO_MARCO * 2)}
            </div>
          </>
        ) : (
          <div className="comemoracao-extenso">{marcoPorExtenso(marco)}</div>
        )}

        {blocos.length === 0 ? (
          /* Pódio vazio tem de DIZER por que, e não sumir: em 13/10 a
             comemoração do sexto milhão saiu sem ele (a página havia carregado
             antes do primeiro ranking) e a tela não dava pista nenhuma de que
             faltava algo. */
          <div className="comemoracao-podio">
            <div className="comemoracao-podio-titulo">
              O RANKING DAS MARCAS ENTRA NO PRÓXIMO CICLO
            </div>
          </div>
        ) : (
          <div className="comemoracao-podio">
            <div className="comemoracao-podio-titulo">
              TOP 3 REPRESENTANTES DE CADA MARCA
            </div>
            {/* DUAS COLUNAS de blocos: 24 linhas numa coluna so davam 1.243 px e
                nao cabiam em 1.080 — medido, e projetor nao rola. */}
            <div className="comemoracao-colunas">
              {blocos.map(bloco => (
                <div className="podio-marca" key={bloco.id}>
                  <div className="podio-marca-nome">{bloco.marca}</div>
                  {bloco.linhas.map(linha => (
                    <div className="comemoracao-linha" key={linha.id}>
                      <span className="comemoracao-posicao">{linha.posicao}º</span>
                      <span className="comemoracao-nome" title={linha.nome}>{linha.nome}</span>
                      <span className="comemoracao-valor-linha">{valorCurto(linha.valor)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* `memo` pelo marco e pela identidade das divisões: o painel por baixo continua
   recebendo snapshot enquanto a comemoração está na tela, e sem isto as 60 peças
   de confete seriam reconciliadas a cada chegada — o que não as reinicia, mas
   gasta reconciliação de graça a cada segundo do relógio. */
export const Comemoracao = memo(
  ComemoracaoBase,
  (antes, depois) =>
    antes.marco === depois.marco
    && antes.total === depois.total
    && antes.meta === depois.meta
    && antes.metade === depois.metade
    && antes.metaGeral === depois.metaGeral
    && antes.divisoes === depois.divisoes,
)
